/**
 * Time conversion utilities for analytics charts
 */

export const TIME_UNITS = {
  SECONDS: 'seconds',
  MINUTES: 'minutes',
};

export const STORAGE_KEY = 'analytics_time_unit_preference';

/**
 * Convert seconds to the target unit
 * @param {number} valueInSeconds - The value in seconds
 * @param {string} targetUnit - 'seconds' or 'minutes'
 * @param {number} decimalPlaces - Number of decimal places (default: 2)
 * @returns {number} Converted value
 */
export function convertTime(valueInSeconds, targetUnit, decimalPlaces = 2) {
  if (valueInSeconds === null || valueInSeconds === undefined) {
    return valueInSeconds;
  }
  if (targetUnit === TIME_UNITS.MINUTES) {
    return Number((valueInSeconds / 60).toFixed(decimalPlaces));
  }
  return Number(Number(valueInSeconds).toFixed(decimalPlaces));
}

/**
 * Convert an array of time values
 * @param {number[]} values - Array of values in seconds
 * @param {string} targetUnit - Target unit
 * @param {number} decimalPlaces - Number of decimal places (default: 2)
 * @returns {number[]} Converted array
 */
export function convertTimeArray(values, targetUnit, decimalPlaces = 2) {
  if (!Array.isArray(values)) return values;
  return values.map((v) => convertTime(v, targetUnit, decimalPlaces));
}

/**
 * Get the unit label for display
 * @param {string} unit - 'seconds' or 'minutes'
 * @param {boolean} abbreviated - Whether to use abbreviated form
 * @returns {string} Unit label
 */
export function getUnitLabel(unit, abbreviated = false) {
  if (unit === TIME_UNITS.MINUTES) {
    return abbreviated ? 'min' : 'minutes';
  }
  return abbreviated ? 's' : 'seconds';
}

/**
 * Save time unit preference to localStorage
 * @param {string} unit - 'seconds' or 'minutes'
 */
export function saveTimeUnitPreference(unit) {
  try {
    localStorage.setItem(STORAGE_KEY, unit);
  } catch (e) {
    console.warn('Failed to save time unit preference:', e);
  }
}

/**
 * Load time unit preference from localStorage
 * @returns {string} Saved preference or default 'seconds'
 */
export function loadTimeUnitPreference() {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved === TIME_UNITS.MINUTES || saved === TIME_UNITS.SECONDS) {
      return saved;
    }
  } catch (e) {
    console.warn('Failed to load time unit preference:', e);
  }
  return TIME_UNITS.SECONDS;
}
